/**
 * Composes the org-profile status banner copy from the server's computed
 * `status`, `blockers` and `underReview` (`PartnerOrgProfileResponse`).
 *
 * It is pure, so the wording stays testable and the page component stays lean.
 * Returns `null` for an `active` org, which shows no banner. A `pending` org
 * gets actionable guidance, or the neutral under-review notice.
 *
 * Two blockers exist: `profile_incomplete`, and `document_unsigned` for
 * unaccepted Terms & Conditions. Either, both or neither may be present on a
 * `pending` org. When the partner's items are done but an admin hasn't
 * verified the org, the server sends `underReview: true` with an empty
 * `blockers` list. The partner then sees a neutral under-review message and
 * never the verification value, so `flagged` and `not_verified` look the same.
 */
import type { PartnerOrgBlocker, PartnerOrgStatus } from "@dearbill/shared";

export interface OrgStatusBanner {
  message: string;
  /**
   * When true, the shell appends a link to the org profile page after the
   * message. A flag rather than JSX keeps this builder pure.
   */
  linkToProfile?: boolean;
}

export function buildOrgStatusBanner(
  status: PartnerOrgStatus,
  blockers: PartnerOrgBlocker[],
  underReview: boolean,
): OrgStatusBanner | null {
  if (status === "active") return null;

  const needsProfile = blockers.includes("profile_incomplete");
  const needsDocs = blockers.includes("document_unsigned");

  let message: string;
  if (needsProfile && needsDocs) {
    message =
      "Complete your organization's profile and agree to DearBill's Terms & Conditions to start enrolling.";
  } else if (needsProfile) {
    return { message: "Complete your organization's profile", linkToProfile: true };
  } else if (needsDocs) {
    message =
      "Agree to DearBill's Terms & Conditions on your organization profile to start enrolling.";
  } else if (underReview) {
    // The partner's items are done and DearBill verification is pending. The
    // copy stays neutral and never reveals the verification value.
    message =
      "Review in progress. Your organization’s setup is complete. You can sign in and explore while DearBill reviews it.";
  } else {
    // Defensive fallback for a pending org with no known blockers that isn't
    // under review; this shouldn't normally occur.
    message = "Your organization isn't active yet.";
  }
  return { message, ...(needsProfile || needsDocs ? { linkToProfile: true } : {}) };
}
